import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Truck, CheckCircle, Clock, Save, Package, X, Camera, Upload, Navigation, AlertTriangle, Radio, Copy, Send, Ban } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { Badge } from '@components/Card';
import { PermissionGuard } from '@components/PermissionGuard';
import { driverRouteService } from '@services/driverRouteService';
import { vehicleService } from '@services/vehicleService';
import { goodsPickupOrderService } from '@services/goodsPickupOrderService';
import { goodsDeliveryOrderService } from '@services/goodsDeliveryOrderService';
import authService from '@services/authService';
import {
  buildDeliveryDraft,
  saveDeliveryDraft,
  deleteDeliveryDraft,
  getActiveDraftForRoute,
} from '@services/deliveryDraftStore';
import { DriverRoute, DriverRouteStop } from '@/types/driverRoute';
import { GoodsPickupOrderItem } from '@/types/goodsPickupOrder';
import { GoodsDeliveryOrderItem } from '@/types/goodsDeliveryOrder';
import { formatQuantity } from '@pages/goodsDeliveryOrders/goodsDeliveryOrderUtils';
import {
  activeLiveTrackingStop,
  driverStopTypeLabel,
  isOwnFleetDeliveryStop,
  isStopClosed,
  stopRefLabel,
  stopTypeBadgeVariant,
  SUSPEND_REASONS,
  usesDeliveryConfirmation,
} from './driverRouteUtils';
import { Vehicle } from '@/types/vehicle';

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '—';
  // Strip Z/offset: backend returns Brasília local time, no UTC conversion should occur
  try { return new Date(dateStr.replace(/Z$|[+-]\d{2}:\d{2}$/, '')).toLocaleString('pt-BR'); } catch { return dateStr; }
};

const formatAddress = (stop: {
  address_street?: string; address_num?: string; address_compl?: string;
  address_district?: string; address_city?: string; address_state?: string;
}) => {
  const parts = [stop.address_street, stop.address_num, stop.address_compl, stop.address_district, stop.address_city, stop.address_state].filter(Boolean);
  return parts.join(', ') || '—';
};

export const DeliveryExecutionDetailView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [route, setRoute] = useState<DriverRoute | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Start delivery form
  const [vehicleKm, setVehicleKm] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [started, setStarted] = useState(false);
  const [kmError, setKmError] = useState('');

  // Stop delivery tracking
  const [deliveringStopId, setDeliveringStopId] = useState<number | null>(null);

  // Pickup confirmation modal (for type R stops)
  const [pickupConfirmStop, setPickupConfirmStop] = useState<DriverRouteStop | null>(null);
  const [pickupItems, setPickupItems] = useState<GoodsPickupOrderItem[]>([]);
  const [pickupItemsLoading, setPickupItemsLoading] = useState(false);
  const [pickupConfirmError, setPickupConfirmError] = useState('');
  const [pickupConfirmSaving, setPickupConfirmSaving] = useState(false);
  const [pickupVolumeQuantity, setPickupVolumeQuantity] = useState<number | null>(null);

  // Delivery confirmation modal (for type E stops)
  const [confirmStop, setConfirmStop] = useState<DriverRouteStop | null>(null);
  const [deliveryContact, setDeliveryContact] = useState('');
  const [deliveryContactDocument, setDeliveryContactDocument] = useState('');
  const [evidencePhoto1, setEvidencePhoto1] = useState<File | null>(null);
  const [evidencePhoto2, setEvidencePhoto2] = useState<File | null>(null);
  const [evidencePreview1, setEvidencePreview1] = useState<string | null>(null);
  const [evidencePreview2, setEvidencePreview2] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState('');
  const [confirmSaving, setConfirmSaving] = useState(false);
  const [restoredDraftNotice, setRestoredDraftNotice] = useState(false);
  // Itens da Ordem de Entrega avulsa (tipo O) exibidos no modal de confirmação
  const [confirmItems, setConfirmItems] = useState<GoodsDeliveryOrderItem[]>([]);
  const [confirmItemsLoading, setConfirmItemsLoading] = useState(false);
  const [confirmItemsError, setConfirmItemsError] = useState('');
  const fileInput1Ref = useRef<HTMLInputElement>(null);
  const fileInput2Ref = useRef<HTMLInputElement>(null);
  const draftRehydratedRef = useRef(false);

  // Shipping provider delivery confirmation modal (simple confirmation, no tracking data)
  const [shippingProviderConfirmStop, setShippingProviderConfirmStop] = useState<DriverRouteStop | null>(null);
  const [shippingProviderConfirmSaving, setShippingProviderConfirmSaving] = useState(false);
  const [shippingProviderConfirmError, setShippingProviderConfirmError] = useState('');

  // ETA for priority stops
  const [etaInputs, setEtaInputs] = useState<Record<number, string>>({});
  const [etaSaving, setEtaSaving] = useState<number | null>(null);

  // Próxima entrega com link ao vivo para o cliente (RN-ROTA-45/46)
  const [liveTrackingBusy, setLiveTrackingBusy] = useState<number | null>(null);
  const [liveTrackingNotice, setLiveTrackingNotice] = useState<{ stopId: number; text: string; ok: boolean } | null>(null);
  const [liveTrackingConfirmStop, setLiveTrackingConfirmStop] = useState<DriverRouteStop | null>(null);

  // Entrega não realizada (RN-ROTA-48)
  const [suspendStops, setSuspendStops] = useState<DriverRouteStop[] | null>(null);
  const [suspendReason, setSuspendReason] = useState<string>('');
  const [suspendOther, setSuspendOther] = useState('');
  const [suspendSaving, setSuspendSaving] = useState(false);
  const [suspendError, setSuspendError] = useState('');

  useEffect(() => {
    if (id) loadData(id);
  }, [id]);

  const loadData = async (routeId: string) => {
    try {
      setLoading(true);
      setError(null);
      const [routeData, vehiclesResult] = await Promise.all([
        driverRouteService.getById(parseInt(routeId)),
        // troca de veículo no início: só os que podem fazer rota
        vehicleService.getAll({ page: 1, pageSize: 100, for_route: true }),
      ]);
      setRoute(routeData);
      const routeVehicle = routeData.vehicle;
      setVehicles(
        routeVehicle && !vehiclesResult.data.some((v) => v.vehicle_id === routeData.vehicle_id)
          ? [...vehiclesResult.data, routeVehicle]
          : vehiclesResult.data,
      );
      setSelectedVehicleId(routeData.vehicle_id);
      if (routeData.vehicle_km) {
        setVehicleKm(routeData.vehicle_km.toString());
        setStarted(true);
      }
      // Re-hidrata um eventual rascunho de confirmação de entrega salvo antes de
      // um re-login forçado (token expirado). Executa apenas uma vez por carga.
      if (!draftRehydratedRef.current) {
        draftRehydratedRef.current = true;
        await rehydrateDeliveryDraft(parseInt(routeId), routeData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar rota');
    } finally {
      setLoading(false);
    }
  };

  // Parada tipo O: carrega os itens da ordem de entrega para o motorista conferir.
  const loadConfirmItems = async (stop: DriverRouteStop) => {
    setConfirmItems([]);
    setConfirmItemsError('');
    if (stop.route_stop_type !== 'O' || !stop.goods_delivery_orders_id) return;
    setConfirmItemsLoading(true);
    try {
      const order = await goodsDeliveryOrderService.getOrderById(stop.goods_delivery_orders_id);
      setConfirmItems(order.items ?? []);
    } catch (err) {
      setConfirmItemsError(err instanceof Error ? err.message : 'Erro ao carregar itens da entrega');
    } finally {
      setConfirmItemsLoading(false);
    }
  };

  // Re-hidrata o rascunho da modal de confirmação de entrega (contato + fotos)
  // persistido em IndexedDB, reabrindo a modal já preenchida para nova submissão.
  const rehydrateDeliveryDraft = async (routeId: number, loadedRoute: DriverRoute) => {
    try {
      const draft = await getActiveDraftForRoute(routeId, Date.now());
      if (!draft) return;

      const stop = loadedRoute.stops?.find(
        (s) => s.driver_route_stop_id === draft.stopId,
      );
      // Stop não existe mais ou já foi entregue: descarta o rascunho.
      if (!stop || isStopClosed(stop) || stop.delivered_at) {
        await deleteDeliveryDraft(draft.routeId, draft.stopId);
        return;
      }

      setConfirmStop(stop);
      void loadConfirmItems(stop);
      setDeliveryContact(draft.delivery_contact || '');
      setDeliveryContactDocument(draft.delivery_contact_document || '');

      if (draft.photo1) {
        setEvidencePhoto1(new File([draft.photo1], 'evidence_1.jpg', { type: draft.photo1.type || 'image/jpeg' }));
        setEvidencePreview1(URL.createObjectURL(draft.photo1));
      }
      if (draft.photo2) {
        setEvidencePhoto2(new File([draft.photo2], 'evidence_2.jpg', { type: draft.photo2.type || 'image/jpeg' }));
        setEvidencePreview2(URL.createObjectURL(draft.photo2));
      }
      setRestoredDraftNotice(true);
    } catch {
      // Falha ao re-hidratar não deve quebrar a tela.
    }
  };

  const handleStartDelivery = async () => {
    if (!vehicleKm.trim() || isNaN(Number(vehicleKm)) || Number(vehicleKm) <= 0) {
      setKmError('Informe a quilometragem atual do veículo');
      return;
    }
    setKmError('');

    try {
      setSaving(true);
      const payload: { vehicle_km: number; vehicle_id?: number } = {
        vehicle_km: Number(vehicleKm),
      };
      if (selectedVehicleId !== route?.vehicle_id) {
        payload.vehicle_id = selectedVehicleId;
      }
      const updated = await driverRouteService.startDelivery(Number(id), payload);
      setRoute(updated);
      setStarted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao iniciar entrega');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkDelivered = async (stopId: number) => {
    // Find the stop
    const stop = route?.stops?.find((s) => s.driver_route_stop_id === stopId);
    if (!stop) return;

    // For delivery stops (type E) with shipping provider, show simple confirmation
    if (stop.route_stop_type === 'E' && stop.shipping_provider_id) {
      setShippingProviderConfirmStop(stop);
      setShippingProviderConfirmError('');
      return;
    }

    // For delivery stops (type E), invitation stops (type D — convite de evento) and
    // avulsa delivery orders (type O), open the confirmation modal (recebedor + fotos).
    // Convite e ordem de entrega NAO disparam notificacao.
    if (usesDeliveryConfirmation(stop.route_stop_type)) {
      setConfirmStop(stop);
      void loadConfirmItems(stop);
      setDeliveryContact('');
      setDeliveryContactDocument('');
      setEvidencePhoto1(null);
      setEvidencePhoto2(null);
      setEvidencePreview1(null);
      setEvidencePreview2(null);
      setConfirmError('');
      return;
    }

    // For pickup stops (type R), open the pickup confirmation modal listing items
    if (stop.route_stop_type === 'R' && stop.goods_pickup_orders_id) {
      setPickupConfirmStop(stop);
      setPickupItems([]);
      setPickupConfirmError('');
      setPickupItemsLoading(true);
      try {
        const pickupOrder = await goodsPickupOrderService.getOrderById(stop.goods_pickup_orders_id);
        setPickupItems(pickupOrder.items ?? []);
        setPickupVolumeQuantity(pickupOrder.volume_quantity ?? null);
      } catch (err) {
        setPickupConfirmError(err instanceof Error ? err.message : 'Erro ao carregar itens da retirada');
      } finally {
        setPickupItemsLoading(false);
      }
      return;
    }

    // For pickup stops (type R), mark directly
    try {
      setDeliveringStopId(stopId);
      await driverRouteService.markStopDelivered(Number(id), stopId);
      const updated = await driverRouteService.getById(Number(id));
      setRoute(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao marcar entrega');
    } finally {
      setDeliveringStopId(null);
    }
  };

  const handleConfirmPickup = async () => {
    if (!pickupConfirmStop) return;
    setPickupConfirmError('');
    setPickupConfirmSaving(true);
    try {
      await driverRouteService.markStopDelivered(Number(id), pickupConfirmStop.driver_route_stop_id);
      setPickupConfirmStop(null);
      const updated = await driverRouteService.getById(Number(id));
      setRoute(updated);
    } catch (err) {
      setPickupConfirmError(err instanceof Error ? err.message : 'Erro ao confirmar retirada');
    } finally {
      setPickupConfirmSaving(false);
    }
  };

  const handleConfirmShippingProviderDelivery = async () => {
    if (!shippingProviderConfirmStop) return;
    setShippingProviderConfirmError('');
    setShippingProviderConfirmSaving(true);
    try {
      await driverRouteService.markStopDelivered(Number(id), shippingProviderConfirmStop.driver_route_stop_id);
      setShippingProviderConfirmStop(null);
      const updated = await driverRouteService.getById(Number(id));
      setRoute(updated);
    } catch (err) {
      setShippingProviderConfirmError(err instanceof Error ? err.message : 'Erro ao confirmar entrega na transportadora');
    } finally {
      setShippingProviderConfirmSaving(false);
    }
  };

  const handleFileSelect = async (file: File | null, slot: 1 | 2) => {
    if (!file) return;
    // Validate image type
    if (!file.type.startsWith('image/')) {
      setConfirmError('Selecione apenas arquivos de imagem');
      return;
    }
    // Max 10MB before compression
    if (file.size > 10 * 1024 * 1024) {
      setConfirmError('A imagem deve ter no máximo 10MB');
      return;
    }
    setConfirmError('');

    // Compress image
    let compressedFile: File;
    try {
      compressedFile = await imageCompression(file, {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 1280,
        useWebWorker: true,
        fileType: 'image/jpeg',
      });
    } catch {
      compressedFile = file;
    }

    const preview = URL.createObjectURL(compressedFile);
    if (slot === 1) {
      if (evidencePreview1) URL.revokeObjectURL(evidencePreview1);
      setEvidencePhoto1(compressedFile);
      setEvidencePreview1(preview);
    } else {
      if (evidencePreview2) URL.revokeObjectURL(evidencePreview2);
      setEvidencePhoto2(compressedFile);
      setEvidencePreview2(preview);
    }
  };

  const handleConfirmDelivery = async () => {
    if (!confirmStop) return;
    if (!deliveryContact.trim()) {
      setConfirmError('Informe o nome do contato que recebeu o pedido');
      return;
    }
    setConfirmError('');
    setConfirmSaving(true);
    const routeId = Number(id);
    const primaryStopId = confirmStop.driver_route_stop_id;
    const payload = {
      delivery_contact: deliveryContact.trim(),
      delivery_contact_document: deliveryContactDocument.trim() || undefined,
      evidence_photo_1: evidencePhoto1,
      evidence_photo_2: evidencePhoto2,
    };

    // Entrega agrupada: todos os pontos de entrega (tipo E) ainda não entregues
    // do mesmo cliente no mesmo endereço são confirmados de uma só vez.
    // Convite (tipo D) e ordem de entrega (tipo O) são sempre individuais — não agrupam com pedidos.
    const stopAddressKey = getAddressKey(confirmStop);
    const groupedStopsToDeliver = confirmStop.route_stop_type === 'E'
      ? (route?.stops?.filter(
          (s) => !isStopClosed(s) && s.route_stop_type === 'E' && getAddressKey(s) === stopAddressKey,
        ) ?? [confirmStop])
      : [confirmStop];

    // Persiste o rascunho ANTES de submeter. Se o token estiver expirado e o app
    // recarregar para re-login, contato + fotos sobrevivem e são re-hidratados ao
    // voltar para esta tela (nada é perdido). O rascunho é salvo na parada que o
    // motorista abriu; ao reenviar, o agrupamento é recalculado e apenas as
    // paradas ainda não entregues do endereço são confirmadas (entregas já
    // efetivadas em uma tentativa parcial não são repetidas).
    await saveDeliveryDraft(
      buildDeliveryDraft(
        routeId,
        primaryStopId,
        {
          delivery_contact: payload.delivery_contact,
          delivery_contact_document: payload.delivery_contact_document,
          photo1: evidencePhoto1,
          photo2: evidencePhoto2,
        },
        Date.now(),
      ),
    );

    try {
      // Refresh proativo do token + cookie de sessão antes de submeter — cobre o
      // caso do celular que ficou em repouso durante a navegação no Waze e cujo
      // access token (≈1h) expirou sem refresh automático.
      await authService.ensureFreshSession();

      // Confirma a entrega de todas as paradas agrupadas (mesmo cliente/endereço).
      for (const stop of groupedStopsToDeliver) {
        await driverRouteService.markStopDeliveredWithTracking(
          routeId,
          stop.driver_route_stop_id,
          payload,
        );
      }

      // Sucesso: o rascunho não é mais necessário.
      await deleteDeliveryDraft(routeId, primaryStopId);

      // Clean up previews
      if (evidencePreview1) URL.revokeObjectURL(evidencePreview1);
      if (evidencePreview2) URL.revokeObjectURL(evidencePreview2);
      setConfirmStop(null);
      setRestoredDraftNotice(false);
      const updated = await driverRouteService.getById(routeId);
      setRoute(updated);
    } catch (err) {
      // O rascunho permanece salvo para nova tentativa (não é apagado aqui).
      setConfirmError(err instanceof Error ? err.message : 'Erro ao confirmar entrega');
    } finally {
      setConfirmSaving(false);
    }
  };

  const closeConfirmModal = () => {
    if (evidencePreview1) URL.revokeObjectURL(evidencePreview1);
    if (evidencePreview2) URL.revokeObjectURL(evidencePreview2);
    // Cancelamento explícito: descarta o rascunho persistido desta parada.
    if (confirmStop) {
      void deleteDeliveryDraft(Number(id), confirmStop.driver_route_stop_id);
    }
    setRestoredDraftNotice(false);
    setConfirmStop(null);
  };

  const openWaze = (stop: DriverRouteStop) => {
    if (stop.latitude != null && stop.longitude != null) {
      window.open(`https://waze.com/ul?ll=${stop.latitude},${stop.longitude}&navigate=yes`, '_blank');
    } else {
      const addr = formatAddress(stop);
      window.open(`https://waze.com/ul?q=${encodeURIComponent(addr)}&navigate=yes`, '_blank');
    }
  };

  const openGoogleMaps = (stop: DriverRouteStop) => {
    if (stop.latitude != null && stop.longitude != null) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${stop.latitude},${stop.longitude}`, '_blank');
    } else {
      const addr = formatAddress(stop);
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`, '_blank');
    }
  };

  const handleSaveETA = async (stop: DriverRouteStop) => {
    const minutes = parseInt(etaInputs[stop.driver_route_stop_id] || '');
    if (isNaN(minutes) || minutes < 0) return;
    setEtaSaving(stop.driver_route_stop_id);
    try {
      await driverRouteService.updateStopETA(Number(id), stop.driver_route_stop_id, minutes);
      const updated = await driverRouteService.getById(Number(id));
      setRoute(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar previsão');
    } finally {
      setEtaSaving(null);
    }
  };

  const handleStartLiveTracking = async (stop: DriverRouteStop) => {
    setLiveTrackingConfirmStop(null);
    setLiveTrackingBusy(stop.driver_route_stop_id);
    setLiveTrackingNotice(null);
    try {
      await authService.ensureFreshSession();
      const resp = await driverRouteService.startLiveTracking(Number(id), stop.driver_route_stop_id);
      setLiveTrackingNotice({
        stopId: stop.driver_route_stop_id,
        ok: resp.whatsapp_queued,
        text: resp.whatsapp_queued
          ? `Cliente avisada: ${resp.whatsapp_message ?? 'mensagem enviada'}.`
          : `Link criado, mas a mensagem não foi enviada (${resp.whatsapp_message ?? 'motivo desconhecido'}). Use "Copiar link".`,
      });
      const updated = await driverRouteService.getById(Number(id));
      setRoute(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao iniciar a entrega com rastreamento');
    } finally {
      setLiveTrackingBusy(null);
    }
  };

  const handleResendLiveTracking = async (stop: DriverRouteStop) => {
    setLiveTrackingBusy(stop.driver_route_stop_id);
    setLiveTrackingNotice(null);
    try {
      const resp = await driverRouteService.resendLiveTracking(Number(id), stop.driver_route_stop_id);
      setLiveTrackingNotice({
        stopId: stop.driver_route_stop_id,
        ok: resp.whatsapp_queued,
        text: resp.whatsapp_queued
          ? `Link reenviado: ${resp.whatsapp_message ?? 'mensagem enviada'}.`
          : `Não foi possível reenviar (${resp.whatsapp_message ?? 'motivo desconhecido'}). Use "Copiar link".`,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao reenviar o link');
    } finally {
      setLiveTrackingBusy(null);
    }
  };

  const handleCopyLiveTrackingLink = async (stop: DriverRouteStop) => {
    const url = stop.live_tracking?.url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setLiveTrackingNotice({ stopId: stop.driver_route_stop_id, ok: true, text: 'Link copiado.' });
    } catch {
      setLiveTrackingNotice({ stopId: stop.driver_route_stop_id, ok: false, text: url });
    }
  };

  const openSuspendModal = (stops: DriverRouteStop[]) => {
    setSuspendStops(stops);
    setSuspendReason('');
    setSuspendOther('');
    setSuspendError('');
  };

  const handleConfirmSuspend = async () => {
    if (!suspendStops || suspendStops.length === 0) return;
    const reason = suspendReason === 'Outro' ? suspendOther.trim() : suspendReason;
    if (!reason) {
      setSuspendError('Informe o motivo da entrega não realizada');
      return;
    }
    setSuspendError('');
    setSuspendSaving(true);
    try {
      await authService.ensureFreshSession();
      // Paradas agrupadas (mesmo cliente e endereço): a cliente recebe uma mensagem só.
      for (const [i, stop] of suspendStops.entries()) {
        await driverRouteService.suspendStop(Number(id), stop.driver_route_stop_id, reason, i === 0);
      }
      setSuspendStops(null);
      const updated = await driverRouteService.getById(Number(id));
      setRoute(updated);
    } catch (err) {
      setSuspendError(err instanceof Error ? err.message : 'Erro ao suspender a entrega');
    } finally {
      setSuspendSaving(false);
    }
  };

  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-slate-500">Carregando rota...</div>
      </div>
    );
  }

  if (error && !route) {
    return (
      <div className="space-y-4 max-w-2xl mx-auto">
        <Button variant="secondary" size="sm" onClick={() => navigate('/delivery')} icon={<ArrowLeft size={20} />}>Voltar</Button>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      </div>
    );
  }

  if (!route) return null;

  const pendingStops = route.stops?.filter((s) => !isStopClosed(s)) ?? [];
  const deliveredStops = route.stops?.filter((s) => s.is_delivered) ?? [];
  const suspendedStops = route.stops?.filter((s) => !s.is_delivered && !!s.suspended_at) ?? [];
  const inRouteStop = activeLiveTrackingStop(route.stops);
  const liveTrackingEnabled = started && !!route.vehicle_has_tracker;
  const hasEligibleNext = liveTrackingEnabled && !inRouteStop && pendingStops.some(isOwnFleetDeliveryStop);

  // Group pending delivery stops (type E) by customer + address for same-address consolidation
  const getAddressKey = (stop: DriverRouteStop) => {
    const parts = [
      stop.customer_name?.toLowerCase().trim(),
      stop.address_street?.toLowerCase().trim(),
      stop.address_num?.toLowerCase().trim(),
      stop.address_city?.toLowerCase().trim(),
      stop.address_state?.toLowerCase().trim(),
    ].filter(Boolean).join('|');
    return parts;
  };

  interface GroupedStop {
    stops: DriverRouteStop[];
    isGroup: boolean;
  }

  const groupedPendingStops: GroupedStop[] = (() => {
    const groups: GroupedStop[] = [];
    const addressMap = new Map<string, DriverRouteStop[]>();
    const nonDeliveryStops: DriverRouteStop[] = [];

    for (const stop of pendingStops) {
      if (stop.route_stop_type === 'E') {
        const key = getAddressKey(stop);
        if (!addressMap.has(key)) addressMap.set(key, []);
        addressMap.get(key)!.push(stop);
      } else {
        nonDeliveryStops.push(stop);
      }
    }

    // Reconstruct in original order
    const processed = new Set<number>();
    for (const stop of pendingStops) {
      if (processed.has(stop.driver_route_stop_id)) continue;
      if (stop.route_stop_type !== 'E') {
        groups.push({ stops: [stop], isGroup: false });
        processed.add(stop.driver_route_stop_id);
      } else {
        const key = getAddressKey(stop);
        const group = addressMap.get(key) ?? [stop];
        for (const s of group) processed.add(s.driver_route_stop_id);
        groups.push({ stops: group, isGroup: group.length > 1 });
      }
    }
    return groups;
  })();

  return (
    <div className="space-y-4 max-w-2xl mx-auto pb-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="secondary" size="sm" onClick={() => navigate('/delivery')} icon={<ArrowLeft size={20} />} />
        <div className="flex-1">
          <h1 className="text-lg font-bold text-slate-900">Rota #{route.driver_route_id}</h1>
          <p className="text-sm text-slate-500">{route.driver?.full_name}</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{error}</div>
      )}

      {/* Start Delivery Card */}
      {!started && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader title={<span className="flex items-center gap-2"><Truck size={18} /> Iniciar Entrega</span>} />
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Veículo</label>
              <select
                value={selectedVehicleId}
                onChange={(e) => setSelectedVehicleId(Number(e.target.value))}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
              >
                {vehicles.map((v) => (
                  <option key={v.vehicle_id} value={v.vehicle_id}>
                    {v.description} — {v.license_plate}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Quilometragem do Veículo *</label>
              <Input
                type="number"
                inputMode="numeric"
                value={vehicleKm}
                onChange={(e) => { setVehicleKm(e.target.value); setKmError(''); }}
                placeholder="Ex: 45230"
                className={`text-lg ${kmError ? 'border-red-500' : ''}`}
              />
              {kmError && <p className="text-red-500 text-sm mt-1">{kmError}</p>}
            </div>
            <PermissionGuard permission="driver_routes.write">
              <Button onClick={handleStartDelivery} disabled={saving} className="w-full" icon={<Save size={18} />}>
                {saving ? 'Salvando...' : 'Iniciar Entrega'}
              </Button>
            </PermissionGuard>
          </CardContent>
        </Card>
      )}

      {/* Vehicle KM info (after started) */}
      {started && (
        <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <Package size={18} className="text-emerald-600" />
          <div className="text-sm">
            <span className="text-slate-600">Veículo: </span>
            <span className="font-medium">{route.vehicle?.description} ({route.vehicle?.license_plate})</span>
            <span className="mx-2">|</span>
            <span className="text-slate-600">KM: </span>
            <span className="font-medium">{route.vehicle_km?.toLocaleString('pt-BR')}</span>
          </div>
        </div>
      )}

      {hasEligibleNext && (
        <div className="flex items-start gap-2 p-3 bg-sky-50 border border-sky-200 rounded-lg text-sm text-sky-800">
          <Radio size={16} className="mt-0.5 flex-shrink-0" />
          <span>
            Escolha a próxima entrega e toque em <strong>Iniciar entrega desta parada</strong>: a cliente recebe pelo
            WhatsApp o link para acompanhar o carro no mapa.
          </span>
        </div>
      )}

      {/* Pending Stops */}
      {pendingStops.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1">
            <Clock size={14} /> Pendentes ({pendingStops.length})
          </h2>
          <div className="space-y-2">
            {groupedPendingStops.map((group) => {
              const primaryStop = group.stops[0];
              const allOrderIds = group.stops.map((s) => s.order_id).filter(Boolean);
              const groupInRoute = group.stops.find((s) => s.live_tracking?.status === 'A');
              const ownFleet = isOwnFleetDeliveryStop(primaryStop);
              const groupKey = group.stops.map((s) => s.driver_route_stop_id).join('-');

              return (
              <Card key={groupKey} className="border-amber-100">
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge variant={stopTypeBadgeVariant(primaryStop.route_stop_type)}>
                          {driverStopTypeLabel(primaryStop.route_stop_type)}
                        </Badge>
                        {primaryStop.route_stop_type === 'O' && primaryStop.is_emergency && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-white bg-red-600 px-1.5 py-0.5 rounded-full">
                            <AlertTriangle size={10} /> Emergência
                          </span>
                        )}
                        {group.isGroup && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-full">
                            {group.stops.length} pedidos
                          </span>
                        )}
                        {groupInRoute && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-white bg-sky-600 px-1.5 py-0.5 rounded-full">
                            <Radio size={10} /> Em rota
                          </span>
                        )}
                        {group.stops.some((s) => s.priority) && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full">
                            ★ Prioridade
                          </span>
                        )}
                        {group.stops.some((s) => s.has_replacement_items) && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-full">
                            ↩ Troca
                          </span>
                        )}
                        {primaryStop.request_delivery_time && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">
                            🕐 {new Date(primaryStop.request_delivery_time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                        {group.isGroup ? (
                          <span className="text-sm font-medium">
                            Pedidos {allOrderIds.map((id) => `#${id}`).join(', ')}
                          </span>
                        ) : (
                          <span className="text-sm font-medium">{stopRefLabel(primaryStop)}</span>
                        )}
                      </div>
                      {primaryStop.customer_name && (
                        <p className="text-sm text-slate-700 font-medium">{primaryStop.customer_name}</p>
                      )}
                      {primaryStop.reference_description && primaryStop.reference_description !== stopRefLabel(primaryStop) && (
                        <p className="text-sm text-slate-600">{primaryStop.reference_description}</p>
                      )}
                      {primaryStop.route_stop_type === 'O' && primaryStop.goods_delivery_description && (
                        <p className="text-sm text-sky-800 bg-sky-50 border border-sky-200 rounded px-2 py-1 mt-1">
                          <span className="font-medium">Descrição:</span> {primaryStop.goods_delivery_description}
                        </p>
                      )}
                      {primaryStop.route_stop_type === 'R' && primaryStop.pickup_description && (
                        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
                          <span className="font-medium">Descrição:</span> {primaryStop.pickup_description}
                        </p>
                      )}
                      <p className="text-xs text-slate-500 mt-1 flex items-start gap-1">
                        <MapPin size={12} className="mt-0.5 flex-shrink-0" />
                        <span>{formatAddress(primaryStop)}</span>
                      </p>
                      {/* Navigation buttons */}
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          type="button"
                          onClick={() => openWaze(primaryStop)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 rounded-lg transition-colors"
                        >
                          <Navigation size={12} /> Waze
                        </button>
                        <button
                          type="button"
                          onClick={() => openGoogleMaps(primaryStop)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg transition-colors"
                        >
                          <MapPin size={12} /> Google Maps
                        </button>
                      </div>
                      {/* Próxima entrega (link ao vivo) e entrega não realizada */}
                      {started && ownFleet && (
                        <PermissionGuard permission="driver_routes.write">
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {liveTrackingEnabled && !groupInRoute && (
                              <button
                                type="button"
                                disabled={!!inRouteStop || liveTrackingBusy === primaryStop.driver_route_stop_id}
                                onClick={() => setLiveTrackingConfirmStop(primaryStop)}
                                title={inRouteStop ? 'Confirme ou suspenda a entrega em andamento antes' : undefined}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-sky-600 hover:bg-sky-700 text-white rounded-lg transition-colors disabled:opacity-40"
                              >
                                <Radio size={12} /> {liveTrackingBusy === primaryStop.driver_route_stop_id ? 'Enviando...' : 'Iniciar entrega desta parada'}
                              </button>
                            )}
                            {groupInRoute && (
                              <>
                                <button
                                  type="button"
                                  disabled={liveTrackingBusy === groupInRoute.driver_route_stop_id}
                                  onClick={() => handleResendLiveTracking(groupInRoute)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 rounded-lg transition-colors disabled:opacity-50"
                                >
                                  <Send size={12} /> Reenviar link
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleCopyLiveTrackingLink(groupInRoute)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-lg transition-colors"
                                >
                                  <Copy size={12} /> Copiar link
                                </button>
                              </>
                            )}
                            <button
                              type="button"
                              onClick={() => openSuspendModal(group.stops)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-white hover:bg-red-50 text-red-700 border border-red-200 rounded-lg transition-colors"
                            >
                              <Ban size={12} /> Não foi possível entregar
                            </button>
                          </div>
                          {liveTrackingNotice && group.stops.some((s) => s.driver_route_stop_id === liveTrackingNotice.stopId) && (
                            <p className={`text-xs mt-1 break-all ${liveTrackingNotice.ok ? 'text-emerald-700' : 'text-amber-700'}`}>
                              {liveTrackingNotice.text}
                            </p>
                          )}
                        </PermissionGuard>
                      )}
                      {/* ETA input for priority stops */}
                      {primaryStop.priority && !primaryStop.is_delivered && started && (
                        <div className="mt-2 flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                          <Clock size={14} className="text-red-500 flex-shrink-0" />
                          <span className="text-xs text-red-700 font-medium whitespace-nowrap">Previsão:</span>
                          <input
                            type="number"
                            inputMode="numeric"
                            min={0}
                            placeholder="min"
                            value={etaInputs[primaryStop.driver_route_stop_id] ?? (primaryStop.estimate_delay_in_minutes?.toString() || '')}
                            onChange={(e) => setEtaInputs(prev => ({ ...prev, [primaryStop.driver_route_stop_id]: e.target.value }))}
                            className="w-16 text-center text-xs border border-red-200 rounded px-1.5 py-1 focus:ring-1 focus:ring-red-400"
                          />
                          <span className="text-xs text-red-600">min</span>
                          <button
                            type="button"
                            disabled={etaSaving === primaryStop.driver_route_stop_id}
                            onClick={() => handleSaveETA(primaryStop)}
                            className="px-2 py-1 text-xs font-medium bg-red-100 hover:bg-red-200 text-red-700 rounded transition-colors disabled:opacity-50"
                          >
                            {etaSaving === primaryStop.driver_route_stop_id ? '...' : 'Salvar'}
                          </button>
                          {primaryStop.estimate_delay_in_minutes != null && (
                            <span className="text-xs text-red-600 ml-1">
                              (ETA: {(() => {
                                const now = new Date();
                                const eta = new Date(now.getTime() + primaryStop.estimate_delay_in_minutes * 60000);
                                return eta.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                              })()})
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {started && (
                      <PermissionGuard permission="driver_routes.write">
                        <Button
                          size="sm"
                          disabled={group.stops.some((s) => deliveringStopId === s.driver_route_stop_id)}
                          onClick={() => handleMarkDelivered(primaryStop.driver_route_stop_id)}
                          icon={<CheckCircle size={16} />}
                          className="flex-shrink-0"
                        >
                          {group.stops.some((s) => deliveringStopId === s.driver_route_stop_id)
                            ? '...'
                            : primaryStop.route_stop_type === 'R' ? 'Retirar' : 'Entregar'}
                        </Button>
                      </PermissionGuard>
                    )}
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Delivered Stops */}
      {deliveredStops.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1">
            <CheckCircle size={14} className="text-emerald-500" /> Entregues ({deliveredStops.length})
          </h2>
          <div className="space-y-2">
            {deliveredStops.map((stop) => (
              <Card key={stop.driver_route_stop_id} className="border-emerald-100 bg-emerald-50/50">
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge variant="success">
                          {driverStopTypeLabel(stop.route_stop_type)}
                        </Badge>
                        {stop.priority && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full">
                            ★ Prioridade
                          </span>
                        )}
                        {stop.has_replacement_items && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-full">
                            ↩ Troca
                          </span>
                        )}
                        {stop.request_delivery_time && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">
                            🕐 {new Date(stop.request_delivery_time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                        <span className="text-sm font-medium text-slate-600">{stopRefLabel(stop)}</span>
                        <span className="text-xs text-emerald-600">
                          {formatDateTime(stop.delivered_at ?? undefined)}
                        </span>
                      </div>
                      {stop.customer_name && (
                        <p className="text-sm text-slate-500">{stop.customer_name}</p>
                      )}
                      {stop.route_stop_type === 'R' && stop.pickup_description && (
                        <p className="text-xs text-amber-600 mt-0.5">{stop.pickup_description}</p>
                      )}
                      <p className="text-xs text-slate-400 mt-1 flex items-start gap-1">
                        <MapPin size={12} className="mt-0.5 flex-shrink-0" />
                        <span>{formatAddress(stop)}</span>
                      </p>
                    </div>
                    <CheckCircle size={20} className="text-emerald-500 flex-shrink-0 mt-1" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Suspended Stops (RN-ROTA-48) */}
      {suspendedStops.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1">
            <Ban size={14} className="text-slate-500" /> Não entregues — suspensas ({suspendedStops.length})
          </h2>
          <div className="space-y-2">
            {suspendedStops.map((stop) => (
              <Card key={stop.driver_route_stop_id} className="border-slate-200 bg-slate-50">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Badge variant="neutral">Suspensa</Badge>
                    <span className="text-sm font-medium text-slate-600">{stopRefLabel(stop)}</span>
                    <span className="text-xs text-slate-500">{formatDateTime(stop.suspended_at ?? undefined)}</span>
                  </div>
                  {stop.customer_name && <p className="text-sm text-slate-500">{stop.customer_name}</p>}
                  {stop.suspend_reason && (
                    <p className="text-sm text-slate-700 mt-1"><span className="font-medium">Motivo:</span> {stop.suspend_reason}</p>
                  )}
                  <p className="text-xs text-slate-400 mt-1">O pedido voltou para a montagem de rotas.</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* All delivered */}
      {pendingStops.length === 0 && (deliveredStops.length > 0 || suspendedStops.length > 0) && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-6 text-center">
            <CheckCircle size={48} className="mx-auto mb-3 text-emerald-500" />
            <p className="text-lg font-semibold text-emerald-700">Todas as entregas concluídas!</p>
            <Button variant="secondary" className="mt-4" onClick={() => navigate('/delivery')}>
              Voltar para lista
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Delivery Confirmation Modal */}
      {confirmStop && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full sm:max-w-lg max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold">Confirmar Entrega</h3>
                <p className="text-sm text-slate-500">
                  {(() => {
                    // Convite e ordem de entrega: sempre individuais ("Convite #N · destinatário").
                    if (confirmStop.route_stop_type !== 'E') {
                      return `${stopRefLabel(confirmStop)}${confirmStop.customer_name ? ` · ${confirmStop.customer_name}` : ''}`;
                    }
                    const stopAddrKey = getAddressKey(confirmStop);
                    const groupStops = route?.stops?.filter(
                      (s) => !isStopClosed(s) && s.route_stop_type === 'E' && getAddressKey(s) === stopAddrKey
                    ) ?? [confirmStop];
                    const orderIds = groupStops.map((s) => s.order_id).filter(Boolean);
                    return orderIds.length > 1
                      ? `Pedidos ${orderIds.map((oid) => `#${oid}`).join(', ')} — ${confirmStop.customer_name || ''}`
                      : `Pedido #${confirmStop.order_id} — ${confirmStop.customer_name || ''}`;
                  })()}
                </p>
              </div>
              <Button variant="secondary" size="sm" onClick={closeConfirmModal} icon={<X size={20} />} />
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-auto p-4 space-y-4">
              {restoredDraftNotice && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-lg text-sm">
                  Recuperamos os dados que você havia preenchido (contato e fotos). Revise e toque em
                  "Confirmar Entrega" para enviar novamente.
                </div>
              )}
              {confirmError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                  {confirmError}
                </div>
              )}

              {/* Itens da Ordem de Entrega avulsa (tipo O) */}
              {confirmStop.route_stop_type === 'O' && (
                <div>
                  {confirmStop.goods_delivery_description && (
                    <div className="bg-sky-50 border border-sky-200 rounded-lg px-3 py-2 mb-3">
                      <p className="text-sm text-sky-800">
                        <span className="font-medium">Descrição da entrega:</span> {confirmStop.goods_delivery_description}
                      </p>
                    </div>
                  )}
                  <label className="block text-sm font-medium text-slate-700 mb-2">Itens a serem entregues</label>
                  {confirmItemsLoading ? (
                    <div className="text-sm text-slate-500 py-3 text-center">Carregando itens...</div>
                  ) : confirmItemsError ? (
                    <div className="bg-amber-50 border border-amber-200 text-amber-700 px-3 py-2 rounded-lg text-sm">
                      {confirmItemsError}
                    </div>
                  ) : confirmItems.length === 0 ? (
                    <div className="text-sm text-slate-500">Nenhum item informado.</div>
                  ) : (
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium text-slate-700">Descrição</th>
                            <th className="text-right px-3 py-2 font-medium text-slate-700">Qtd</th>
                          </tr>
                        </thead>
                        <tbody>
                          {confirmItems.map((item) => (
                            <tr key={item.goods_delivery_orders_item_id} className="border-t border-slate-100">
                              <td className="px-3 py-2 text-slate-700">{item.description}</td>
                              <td className="px-3 py-2 text-right text-slate-800 whitespace-nowrap">
                                {formatQuantity(item.quantity)}{item.unit ? ` ${item.unit}` : ''}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Delivery Date (auto) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Data da Entrega</label>
                <div className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-600">
                  {new Date().toLocaleString('pt-BR')}
                </div>
                <p className="text-xs text-slate-400 mt-1">Preenchida automaticamente pelo sistema</p>
              </div>

              {/* Delivery Contact (required) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Contato que recebeu <span className="text-red-500">*</span>
                </label>
                <Input
                  value={deliveryContact}
                  onChange={(e) => setDeliveryContact(e.target.value)}
                  placeholder="Nome de quem recebeu o pedido"
                />
              </div>

              {/* Delivery Contact Document */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Documento do contato</label>
                <Input
                  value={deliveryContactDocument}
                  onChange={(e) => setDeliveryContactDocument(e.target.value)}
                  placeholder="CPF ou RG (opcional)"
                  maxLength={15}
                />
              </div>

              {/* Volume Quantity */}
              {confirmStop.volume_quantity != null && confirmStop.volume_quantity > 0 && (
                <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <Package size={16} className="text-blue-600 flex-shrink-0" />
                  <span className="text-sm text-blue-800">
                    <span className="font-medium">Volumes:</span> {confirmStop.volume_quantity}
                  </span>
                </div>
              )}

              {/* Evidence Photos */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Fotos de evidência</label>
                <div className="grid grid-cols-2 gap-3">
                  {/* Photo 1 */}
                  <div className="relative">
                    <input
                      ref={fileInput1Ref}
                      type="file"
                      accept="image/*"
                      capture={isMobile ? 'environment' : undefined}
                      className="hidden"
                      onChange={(e) => handleFileSelect(e.target.files?.[0] ?? null, 1)}
                    />
                    {evidencePreview1 ? (
                      <div className="relative group">
                        <img
                          src={evidencePreview1}
                          alt="Evidência 1"
                          className="w-full h-32 object-cover rounded-lg border border-slate-200"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (evidencePreview1) URL.revokeObjectURL(evidencePreview1);
                            setEvidencePhoto1(null);
                            setEvidencePreview1(null);
                            if (fileInput1Ref.current) fileInput1Ref.current.value = '';
                          }}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileInput1Ref.current?.click()}
                        className="w-full h-32 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-2 text-slate-400 hover:border-emerald-400 hover:text-emerald-500 transition-colors"
                      >
                        {isMobile ? <Camera size={28} /> : <Upload size={28} />}
                        <span className="text-xs">Foto 1</span>
                      </button>
                    )}
                  </div>

                  {/* Photo 2 */}
                  <div className="relative">
                    <input
                      ref={fileInput2Ref}
                      type="file"
                      accept="image/*"
                      capture={isMobile ? 'environment' : undefined}
                      className="hidden"
                      onChange={(e) => handleFileSelect(e.target.files?.[0] ?? null, 2)}
                    />
                    {evidencePreview2 ? (
                      <div className="relative group">
                        <img
                          src={evidencePreview2}
                          alt="Evidência 2"
                          className="w-full h-32 object-cover rounded-lg border border-slate-200"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (evidencePreview2) URL.revokeObjectURL(evidencePreview2);
                            setEvidencePhoto2(null);
                            setEvidencePreview2(null);
                            if (fileInput2Ref.current) fileInput2Ref.current.value = '';
                          }}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => fileInput2Ref.current?.click()}
                        className="w-full h-32 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-2 text-slate-400 hover:border-emerald-400 hover:text-emerald-500 transition-colors"
                      >
                        {isMobile ? <Camera size={28} /> : <Upload size={28} />}
                        <span className="text-xs">Foto 2</span>
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  {isMobile ? 'Toque para tirar uma foto com a câmera' : 'Clique para fazer upload de imagens'}
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t flex gap-3">
              <Button variant="secondary" onClick={closeConfirmModal} className="flex-1">
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmDelivery}
                disabled={confirmSaving}
                icon={<CheckCircle size={18} />}
                className="flex-1"
              >
                {confirmSaving ? 'Confirmando...' : 'Confirmar Entrega'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pickup Confirmation Modal (type R) */}
      {pickupConfirmStop && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full sm:max-w-lg max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold">Confirmar Retirada</h3>
                <p className="text-sm text-slate-500">
                  Retirada #{pickupConfirmStop.goods_pickup_orders_id}
                  {pickupConfirmStop.reference_description ? ` — ${pickupConfirmStop.reference_description}` : ''}
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPickupConfirmStop(null)}
                disabled={pickupConfirmSaving}
                icon={<X size={20} />}
              />
            </div>

            {/* Body */}
            <div className="flex-1 overflow-auto p-4 space-y-4">
              {pickupConfirmError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                  {pickupConfirmError}
                </div>
              )}

              {/* Pickup Description */}
              {pickupConfirmStop.pickup_description && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <p className="text-sm text-amber-800">
                    <span className="font-medium">Descrição da retirada:</span> {pickupConfirmStop.pickup_description}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Itens a serem retirados
                </label>
                {pickupItemsLoading ? (
                  <div className="text-sm text-slate-500 py-4 text-center">Carregando itens...</div>
                ) : pickupItems.length === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 text-amber-700 px-3 py-2 rounded-lg text-sm">
                    Nenhum item catalogado para esta retirada. Confirme apenas se a retirada física foi realizada.
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-slate-700">SKU</th>
                          <th className="text-left px-3 py-2 font-medium text-slate-700">Descrição</th>
                          <th className="text-right px-3 py-2 font-medium text-slate-700">Qtd</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pickupItems.map((item, idx) => (
                          <tr key={item.goods_pickup_orders_item_id ?? `${item.sku}-${idx}`} className="border-t border-slate-100">
                            <td className="px-3 py-2 font-mono text-slate-800">{item.sku}</td>
                            <td className="px-3 py-2 text-slate-700">{item.description ?? '—'}</td>
                            <td className="px-3 py-2 text-right text-slate-800">{item.quantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {pickupVolumeQuantity != null && pickupVolumeQuantity > 0 && (
                <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <Package size={16} className="text-blue-600 flex-shrink-0" />
                  <span className="text-sm text-blue-800">
                    <span className="font-medium">Volumes esperados:</span> {pickupVolumeQuantity}
                  </span>
                </div>
              )}

              <p className="text-xs text-slate-500">
                Confirme abaixo que todos os itens foram retirados com sucesso. A retirada será marcada como concluída.
              </p>
            </div>

            {/* Footer */}
            <div className="p-4 border-t flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setPickupConfirmStop(null)}
                disabled={pickupConfirmSaving}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmPickup}
                disabled={pickupConfirmSaving || pickupItemsLoading}
                icon={<CheckCircle size={18} />}
                className="flex-1"
              >
                {pickupConfirmSaving ? 'Confirmando...' : 'Confirmar Retirada'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Próxima entrega: confirmação do envio do link (RN-ROTA-45) */}
      {liveTrackingConfirmStop && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full sm:max-w-lg flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold">Iniciar entrega desta parada</h3>
                <p className="text-sm text-slate-500">
                  {stopRefLabel(liveTrackingConfirmStop)}{liveTrackingConfirmStop.customer_name ? ` — ${liveTrackingConfirmStop.customer_name}` : ''}
                </p>
              </div>
              <Button variant="secondary" size="sm" onClick={() => setLiveTrackingConfirmStop(null)} icon={<X size={20} />} />
            </div>
            <div className="p-4 space-y-2 text-sm text-slate-700">
              <p>A cliente vai receber pelo WhatsApp um link para acompanhar o carro no mapa até você confirmar a entrega.</p>
              <p className="text-slate-500">Ela verá o seu primeiro nome, o modelo e a placa do veículo.</p>
            </div>
            <div className="p-4 border-t flex gap-3">
              <Button variant="secondary" onClick={() => setLiveTrackingConfirmStop(null)} className="flex-1">Cancelar</Button>
              <Button onClick={() => handleStartLiveTracking(liveTrackingConfirmStop)} icon={<Radio size={18} />} className="flex-1">
                Avisar a cliente
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Entrega não realizada (RN-ROTA-48) */}
      {suspendStops && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full sm:max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold">Não foi possível entregar</h3>
                <p className="text-sm text-slate-500">
                  {suspendStops.length > 1
                    ? `Pedidos ${suspendStops.map((s) => `#${s.order_id}`).join(', ')}`
                    : stopRefLabel(suspendStops[0])}
                  {suspendStops[0].customer_name ? ` — ${suspendStops[0].customer_name}` : ''}
                </p>
              </div>
              <Button variant="secondary" size="sm" onClick={() => setSuspendStops(null)} disabled={suspendSaving} icon={<X size={20} />} />
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-3">
              {suspendError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{suspendError}</div>
              )}
              <p className="text-sm font-medium text-slate-700">Motivo *</p>
              <div className="space-y-2">
                {[...SUSPEND_REASONS, 'Outro'].map((r) => (
                  <label key={r} className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="radio" name="suspend-reason" value={r} checked={suspendReason === r} onChange={() => setSuspendReason(r)} />
                    {r}
                  </label>
                ))}
              </div>
              {suspendReason === 'Outro' && (
                <Input value={suspendOther} onChange={(e) => setSuspendOther(e.target.value)} maxLength={300} placeholder="Descreva o motivo" />
              )}
              <div className="bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-lg text-sm">
                A parada sai das pendências desta rota, a cliente recebe um WhatsApp avisando que a entrega será
                reagendada e o pedido volta para a montagem de rotas. Isso não pode ser desfeito nesta rota.
              </div>
            </div>
            <div className="p-4 border-t flex gap-3">
              <Button variant="secondary" onClick={() => setSuspendStops(null)} disabled={suspendSaving} className="flex-1">Cancelar</Button>
              <Button onClick={handleConfirmSuspend} disabled={suspendSaving} icon={<Ban size={18} />} className="flex-1">
                {suspendSaving ? 'Salvando...' : 'Suspender entrega'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Shipping Provider Delivery Confirmation Modal */}
      {shippingProviderConfirmStop && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full sm:max-w-lg max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold">Confirmar Entrega na Transportadora</h3>
                <p className="text-sm text-slate-500">
                  Pedido #{shippingProviderConfirmStop.order_id} — {shippingProviderConfirmStop.customer_name || ''}
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShippingProviderConfirmStop(null)}
                disabled={shippingProviderConfirmSaving}
                icon={<X size={20} />}
              />
            </div>

            {/* Body */}
            <div className="flex-1 overflow-auto p-4 space-y-4">
              {shippingProviderConfirmError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                  {shippingProviderConfirmError}
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Truck size={18} className="text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">
                    Transportadora: {shippingProviderConfirmStop.shipping_provider_name || `#${shippingProviderConfirmStop.shipping_provider_id}`}
                  </span>
                </div>
                <p className="text-sm text-blue-700">
                  Os pedidos foram entregues no ponto de coleta da transportadora?
                </p>
                {shippingProviderConfirmStop.volume_quantity != null && shippingProviderConfirmStop.volume_quantity > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    <Package size={16} className="text-blue-600 flex-shrink-0" />
                    <span className="text-sm text-blue-800">
                      <span className="font-medium">Volumes entregues:</span> {shippingProviderConfirmStop.volume_quantity}
                    </span>
                  </div>
                )}
              </div>

              <p className="text-xs text-slate-500">
                Ao confirmar, o pedido será marcado como entregue na transportadora. O status do pedido <strong>não</strong> será alterado para "Entregue" — o processo de entrega segue com a transportadora.
              </p>
            </div>

            {/* Footer */}
            <div className="p-4 border-t flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setShippingProviderConfirmStop(null)}
                disabled={shippingProviderConfirmSaving}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmShippingProviderDelivery}
                disabled={shippingProviderConfirmSaving}
                icon={<CheckCircle size={18} />}
                className="flex-1"
              >
                {shippingProviderConfirmSaving ? 'Confirmando...' : 'Confirmar Entrega'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
